// ─── Constants (mirrors constants.json in the CLI) ────────────────────────────

const LUMINANCE_HEIGHT = 640
const MINIMUM_WIDTH = 480
const MINIMUM_HEIGHT = 640

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ImageTargetEntry {
  imagePath: string
  name: string
  type: 'PLANAR'
  properties: {
    left: number
    top: number
    width: number
    height: number
    isRotated: boolean
    originalWidth: number
    originalHeight: number
  }
  resources: {
    originalImage: string
    croppedImage: string
    thumbnailImage: string
    luminanceImage: string
  }
  metadata: null
  created: number
  updated: number
}

export interface ProcessedTarget {
  /** Completed metadata entry — imagePath is fully set */
  data: ImageTargetEntry
  /** Luminance image Blob, ready to upload via s3Client.upload() */
  luminanceBlob: Blob
  /** S3 key to pass to s3Client.upload(key, blob) */
  luminanceKey: string
}

export interface ProcessingResult {
  /** One entry per source image, in the order they were passed in */
  targets: ProcessedTarget[]
  /** targets.json Blob, ready to upload via s3Client.upload() */
  targetsJsonBlob: Blob
  /** S3 key to pass to s3Client.upload(key, blob) */
  targetsJsonKey: string
  /** Public URL of targets.json — use this as stage_data.mind */
  targetsJsonUrl: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Builds the full public S3 URL for a given S3 key.
 * Relies on REACT_APP_AWS_S3_BASE_URL being set in the environment.
 */
export function toPublicUrl(key: string): string {
  const base = process.env.REACT_APP_AWS_S3_BASE_URL ?? ''
  return `${base}/${key}`
}

/**
 * Normalizes a project ID for safe use as an S3 path segment:
 * trims whitespace, lowercases, and replaces any remaining spaces with underscores.
 */
function normalizeProjectId(projectId: string): string {
  return projectId.trim().toLowerCase().replace(/\s+/g, '_')
}

/**
 * Constructs the S3 key for a file in the xr-targets folder for a given project.
 * Pattern: {REACT_APP_AWS_S3_DIR}/xr-targets/{projectId}/{filename}
 */
function buildXrKey(projectId: string, filename: string): string {
  const dir = process.env.REACT_APP_AWS_S3_DIR ?? ''
  return `${dir}/xr-targets/${normalizeProjectId(projectId)}/${filename}`
}

/**
 * Detects the file extension from a URL, ignoring any query string
 * (e.g. presigned URL parameters).
 */
function detectExtension(url: string): 'png' | 'jpg' {
  const path = url.split('?')[0].toLowerCase()
  return path.endsWith('.png') ? 'png' : 'jpg'
}

/**
 * Detects whether the source image should be stored as "rotated" (i.e. the
 * physical image is landscape but should be treated as portrait by the engine).
 * Mirrors the CLI's isRotated = width >= height logic.
 */
function detectIsRotated(width: number, height: number): boolean {
  return width >= height
}

/**
 * Loads a URL into an HTMLImageElement. crossOrigin is set to 'anonymous'
 * so canvas operations don't taint for S3 public images.
 */
function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`))
    img.src = url
  })
}

// ─── Core ─────────────────────────────────────────────────────────────────────

/**
 * Processes a single image URL into a luminance blob and a fully populated
 * metadata entry using the Canvas API. imagePath is computed from the
 * deterministic S3 key and set immediately — no post-upload patching required.
 */
async function processOneImage(
  url: string,
  index: number,
  projectId: string
): Promise<ProcessedTarget> {
  const img = await loadImageElement(url)

  const srcWidth = img.naturalWidth
  const srcHeight = img.naturalHeight

  if (srcWidth < MINIMUM_WIDTH || srcHeight < MINIMUM_HEIGHT) {
    throw new Error(
      `Image ${index} is too small (${srcWidth}×${srcHeight}). ` +
      `Minimum required: ${MINIMUM_WIDTH}×${MINIMUM_HEIGHT}.`
    )
  }

  const isRotated = detectIsRotated(srcWidth, srcHeight)
  const cropWidth = isRotated ? srcHeight : srcWidth
  const cropHeight = isRotated ? srcWidth : srcHeight

  const name = `target-${index}`
  const extension = detectExtension(url)
  const luminanceFilename = `${name}_luminance.${extension}`
  const mimeType = extension === 'png' ? 'image/png' : 'image/jpeg'

  // Resize to LUMINANCE_HEIGHT, preserving aspect ratio
  const scale = LUMINANCE_HEIGHT / srcHeight
  const scaledWidth = Math.round(srcWidth * scale)

  const canvas = document.createElement('canvas')
  canvas.width = scaledWidth
  canvas.height = LUMINANCE_HEIGHT

  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0, scaledWidth, LUMINANCE_HEIGHT)

  // Convert to grayscale using the standard luminance formula
  const imageData = ctx.getImageData(0, 0, scaledWidth, LUMINANCE_HEIGHT)
  const pixels = imageData.data
  for (let i = 0; i < pixels.length; i += 4) {
    const gray = Math.round(0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2])
    pixels[i] = gray
    pixels[i + 1] = gray
    pixels[i + 2] = gray
    // alpha channel (pixels[i + 3]) is left unchanged
  }
  ctx.putImageData(imageData, 0, 0)

  const luminanceBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error(`Failed to export canvas for image ${index}`)),
      mimeType
    )
  })

  const luminanceKey = buildXrKey(projectId, luminanceFilename)
  const imagePath = toPublicUrl(luminanceKey)

  const entry: ImageTargetEntry = {
    type: 'PLANAR',
    properties: {
      left: 0,
      top: 0,
      width: cropWidth,
      height: cropHeight,
      isRotated,
      originalWidth: cropWidth,
      originalHeight: cropHeight,
    },
    imagePath,
    metadata: null,
    name,
    resources: {
      originalImage: `${name}_original.${extension}`,
      croppedImage: `${name}_cropped.${extension}`,
      thumbnailImage: `${name}_thumbnail.${extension}`,
      luminanceImage: luminanceFilename,
    },
    created: Date.now(),
    updated: Date.now(),
  }

  return { data: entry, luminanceBlob, luminanceKey }
}

/**
 * Entry point. Processes a batch of 1+ public S3 image URLs in order and
 * returns everything needed to upload the results in a single Promise.all.
 *
 * Usage in ProjectForm:
 *   const urls = imageTargets.map(t => toPublicUrl(t.image2 ?? t.image))
 *   const result = await processImageTargetBatch(urls, projectId)
 *   await Promise.all([
 *     ...result.targets.map(t => s3Client.upload(t.luminanceKey, t.luminanceBlob)),
 *     s3Client.upload(result.targetsJsonKey, result.targetsJsonBlob),
 *   ])
 *   // result.targetsJsonUrl is the URL to store in stage_data.mind
 *
 * @param urls       Public S3 URLs of the source images, in the desired target order.
 *                   target-0 = urls[0], target-1 = urls[1], etc.
 *                   Pass image2 ?? image for each target (mirrors the MindAR compiler).
 * @param projectId  The project ID — determines the xr-targets/{projectId}/ S3 path.
 */
export async function processImageTargetBatch(
  urls: Array<string>,
  projectId: string
): Promise<ProcessingResult> {
  if (!urls || urls.length === 0) {
    throw new Error('At least one image URL is required')
  }
  if (!projectId) {
    throw new Error('projectId is required to build the xr-targets S3 path')
  }

  const targets: ProcessedTarget[] = []

  for (let i = 0; i < urls.length; i++) {
    const processed = await processOneImage(urls[i], i, projectId)
    targets.push(processed)
  }

  const targetsJsonContent = JSON.stringify(targets.map(t => t.data), null, 2)
  const targetsJsonBlob = new Blob([targetsJsonContent], { type: 'application/json' })
  const targetsJsonKey = buildXrKey(projectId, 'targets.json')
  const targetsJsonUrl = toPublicUrl(targetsJsonKey)

  return { targets, targetsJsonBlob, targetsJsonKey, targetsJsonUrl }
}
